import React from 'react'
import { useRouter } from 'next/router'

import { useSatuEventTracker } from 'utils/satuEvent'
import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'
import { APP_DEVICE_ID_STORAGE_KEY } from 'configs/android'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useProfile } from './useProfile'
import { useProfileInfo } from './useProfileInfo'

import type {
  TSETrackProfileActivities,
  TProfileSocialMedia,
  TProfileTabsType,
} from 'types/profile'

type TSETrackEventDefaultAtributes = {
  screen_name: string
  is_login: [boolean, 'bool']
  is_webview: [boolean, 'bool']
  email: string
  profile_user_id: string
}

const SATU_EVENT_NAME = 'guru_core_pwa_event_tracker_v1'
const TRACKER_EVENT_NAME = 'GURU_CORE_APP'
const TRACKER_SCREEN_NAME = 'PROFILE_PAGE'
const DEFAULT_TIMEOUT = 5000 // 5 seconds
const normalizeSourcePath = (path: string) => {
  if (!path) return ''

  const [pathWithoutQuery] = path.split(/[?#]/)
  return pathWithoutQuery.replace(/\/+$/, '') || '/'
}

export const useSETrackEvent = () => {
  const { query } = useRouter()
  const source = query?.from as string
  const [userRole, setUserRole] = React.useState<
    'kepala_sekolah' | 'penggerak_komunitas' | ''
  >('')
  const [, , , getAppDeviceIdValue] = useLocalStorage(
    APP_DEVICE_ID_STORAGE_KEY,
    null
  )
  const {
    isNarasumber,
    isNarasumberContactable,
    profileId,
    profileData,
    pelatihanMandiriBadgeCount,
    aksiNyataCount,
    buktiKaryaCount,
    ceritaPraktikCount,
    hasCommunity,
  } = useProfile()

  const { hasProfileInfo, profileInfoData } = useProfileInfo()

  const appDeviceID = getAppDeviceIdValue()
  const hasAppDeviceID = Boolean(appDeviceID)
  const androidHandler = AndroidHandler()
  const { session } = useAuth()
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const EVENT_TRIGGER_MAPPING = {
    '/': 'HOMEPAGE',
    '/home': 'HOMEPAGE',
    '/akun': 'ACCOUNT',
    '/info-terkini': 'INFORMATION_PAGE',
    '/pencarian': 'SEARCH_PAGE',
    '/cerita-praktik': 'CERITA_PRAKTIK_PAGE',
    '/bukti-karya': 'BUKTI_KARYA_PAGE',
    '/komunitas/narasumber': 'NARASUMBER_EXPLORATION_TAB',
    '/komunitas': 'COMMUNITY_DETAILS',
  }

  React.useEffect(() => {
    if (Boolean(session) && isNarasumberContactable) {
      if ((session?.user?.email ?? '').includes('@admin')) {
        setUserRole('kepala_sekolah')
      } else {
        setUserRole('penggerak_komunitas')
      }
    }

    return () => {
      setUserRole('')
    }
  }, [isNarasumberContactable, session])

  const getDefaultEventTrigger = () => {
    const normalizedSource = normalizeSourcePath(source)
    return EVENT_TRIGGER_MAPPING?.[normalizedSource] ?? 'EXTERNAL_LINK'
  }

  const SETrackEventDefaultAtributes: TSETrackEventDefaultAtributes = {
    screen_name: TRACKER_SCREEN_NAME,
    is_login: [Boolean(session), 'bool'],
    is_webview: [androidHandler.enabled, 'bool'],
    email: session?.user?.email ?? null,
    profile_user_id: profileId,
  }

  const SETrackComponent = (
    activity_type: TSETrackProfileActivities,
    additionalAttributes?: Record<string, any>
  ): void => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        activity_type,
        event_name: TRACKER_EVENT_NAME,
        user_id: session?.user?.id,
        ...(hasAppDeviceID ? { device_id: appDeviceID } : {}),
        attributes: SETransformAttrs({
          ...SETrackEventDefaultAtributes,
          event_trigger: TRACKER_SCREEN_NAME,
          ...additionalAttributes,
        }),
      },
      timeout: DEFAULT_TIMEOUT,
      keepAlive: true,
    })
  }

  const SETrackPageLoaded = (): void => {
    SETrackComponent('OPEN_PROFILE_PAGE', {
      event_trigger: getDefaultEventTrigger(),
      is_profile_narasumber: [isNarasumber, 'bool'],
      num_mil_badge: [pelatihanMandiriBadgeCount, 'int'],
      num_aksi_nyata: [aksiNyataCount, 'int'],
      num_cerita_praktik: [ceritaPraktikCount, 'int'],
      bukti_karya: JSON.stringify(profileData?.buktiKarya ?? []),
      toolkits: JSON.stringify(profileData?.toolkits ?? []),
    })
  }

  type TSETrackClickShareProfileProps = {
    pageElement: 'header-button' | 'button'
  }
  const SETrackClickShareProfile = ({
    pageElement,
  }: TSETrackClickShareProfileProps): void => {
    SETrackComponent('CLICK_SHARE_PROFILE', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      page_element: pageElement,
    })
  }

  type TSETrackClickAksiNyataItemProps = {
    aksiNyataLocation: number
    aksiNyataId: string
  }
  const SETrackClickAksiNyataItem = ({
    aksiNyataLocation,
    aksiNyataId,
  }: TSETrackClickAksiNyataItemProps): void => {
    SETrackComponent('CLICK_AKSI_NYATA_PROFILE_DETAIL', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      num_aksi_nyata: [aksiNyataCount, 'int'],
      aksi_nyata_location: [aksiNyataLocation, 'int'],
      aksi_nyata_id: aksiNyataId,
    })
  }

  type TSETrackClickBuktiKaryaCategoryProps = {
    buktiKaryaCategory: string
  }
  const SETrackClickBuktiKaryaCategory = ({
    buktiKaryaCategory,
  }: TSETrackClickBuktiKaryaCategoryProps): void => {
    const currentCategory = (profileData?.buktiKarya ?? []).find(
      ({ category }) => category === buktiKaryaCategory
    )
    SETrackComponent('CLICK_BUKTI_KARYA_PROFILE_CATEGORY', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      num_bukti_karya: [buktiKaryaCount, 'int'],
      bukti_karya_category: buktiKaryaCategory,
      num_category_bukti_karya: [currentCategory?.count ?? 0, 'int'],
    })
  }

  type TSETrackClickBuktiKaryaItemProps = {
    buktiKaryaCategory: string
    buktiKaryaLocation: number
    buktiKaryaId: string
  }
  const SETrackClickBuktiKaryaItem = ({
    buktiKaryaCategory,
    buktiKaryaLocation,
    buktiKaryaId,
  }: TSETrackClickBuktiKaryaItemProps): void => {
    const currentCategory = (profileData?.buktiKarya ?? []).find(
      ({ category }) => category === buktiKaryaCategory
    )
    SETrackComponent('CLICK_BUKTI_KARYA_PROFILE_DETAIL', {
      event_trigger: 'BUKTI_KARYA_PROFILE_PAGE',
      screen_name: 'BUKTI_KARYA_PROFILE_PAGE',
      bukti_karya_category: buktiKaryaCategory,
      num_category_bukti_karya: [currentCategory?.count ?? 0, 'int'],
      bukti_karya_location: [buktiKaryaLocation, 'int'],
      bukti_karya_id: buktiKaryaId,
    })
  }

  type TSETrackClickCeritaPraktikItemProps = {
    ceritaPraktikLocation: number
    ceritaPraktikId: string
  }
  const SETrackClickCeritaPraktikItem = ({
    ceritaPraktikLocation,
    ceritaPraktikId,
  }: TSETrackClickCeritaPraktikItemProps): void => {
    SETrackComponent('CLICK_CERITA_PRAKTIK_PROFILE_DETAIL', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      num_cerita_praktik: [ceritaPraktikCount, 'int'],
      cerita_praktik_location: [ceritaPraktikLocation, 'int'],
      content_id: ceritaPraktikId,
    })
  }

  const SETrackClickEmptyStatePelatihanMandiri = (): void => {
    SETrackComponent('CLICK_MIL_PROFILE_BUTTON')
  }

  const SETrackClickEmptyStateBuktiKarya = (): void => {
    SETrackComponent('CLICK_BUKTI_KARYA_PROFILE_BUTTON')
  }

  const SETrackClickEmptyStateToolkit = (): void => {
    SETrackComponent('CLICK_PERANGKAT_AJAR_PROFILE_BUTTON')
  }

  const SETrackClickContactButton = (): void => {
    SETrackComponent('CLICK_CONTACT_PROFILE', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      user_role: userRole,
    })
  }

  const SETrackCloseContactDrawer = (): void => {
    SETrackComponent('CLOSE_CONTACT_PROFILE', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      user_role: userRole,
    })
  }

  type TSETrackClickContactItemProps = {
    contactType: 'email' | 'whatsapp' | 'clipboard' | 'share_profile'
  }
  const SETrackClickContactItem = ({
    contactType,
  }: TSETrackClickContactItemProps): void => {
    SETrackComponent('CLICK_CONTACT_PROFILE_DETAIL', {
      is_profile_narasumber: [isNarasumber, 'bool'],
      user_role: userRole,
      contact_type: contactType,
    })
  }

  type TSETrackClickProfileTabProps = {
    pageElement: TProfileTabsType
  }
  const SETrackClickProfileTab = ({
    pageElement,
  }: TSETrackClickProfileTabProps): void => {
    SETrackComponent('CLICK_PROFILE_TAB', {
      page_element: pageElement,
      is_profile_info_exist: [hasProfileInfo, 'bool'],
      is_community_info_exist: [hasCommunity, 'bool'],
      social_media: Object.keys(profileInfoData?.socialMedia ?? {})
        .filter((key) => {
          if (Boolean(profileInfoData?.socialMedia?.[key])) {
            return key
          }
        })
        .join(','),
    })
  }

  type TSETrackClickSocialMediaProps = {
    socialMedia: keyof TProfileSocialMedia
  }
  const SETrackClickSocialMedia = ({
    socialMedia,
  }: TSETrackClickSocialMediaProps): void => {
    SETrackComponent('CLICK_SOCIAL_MEDIA_LINK', {
      social_media: socialMedia,
    })
  }

  // Community
  const SETrackClickCommunityItem = (): void => {
    SETrackComponent('CLICK_VIEW_COMMUNITY_DETAILS')
  }

  const SETrackClickCommunityShowAll = (): void => {
    SETrackComponent('CLICK_YOUR_COMMUNITIES')
  }

  return {
    SETrackPageLoaded,
    SETrackClickShareProfile,

    // Karya
    SETrackClickAksiNyataItem,
    SETrackClickBuktiKaryaCategory,
    SETrackClickBuktiKaryaItem,
    SETrackClickCeritaPraktikItem,

    // Empty State tracker
    SETrackClickEmptyStatePelatihanMandiri,
    SETrackClickEmptyStateBuktiKarya,
    SETrackClickEmptyStateToolkit,

    // Contact Narasumber tracker
    SETrackClickContactButton,
    SETrackCloseContactDrawer,
    SETrackClickContactItem,

    // Tab
    SETrackClickProfileTab,

    // Info
    SETrackClickSocialMedia,
    SETrackClickCommunityItem,
    SETrackClickCommunityShowAll,
  }
}
